import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
// Import TanStack Table
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender, // Utility for rendering headers/cells
} from '@tanstack/react-table';
import { useTheme } from '../../contexts/ThemeContext'; // Correctly import useTheme
import { TableContextProvider, useTableContext } from './TableManager/context/TableContext';
import { DataTable } from './TableManager/table/DataTable';
import { Pagination } from './TableManager/table/Pagination';
import EditRecordForm from './TableManager/forms/EditRecordForm';
import NewRecordForm from './TableManager/forms/NewRecordForm';
import { GenericFilterPanel } from './TableManager/filters/GenericFilterPanel';
import { ConflictResolution } from './TableManager/ui/ConflictResolution';
import MinimizedEditForm from './TableManager/forms/MinimizedEditForm';
import { formatErrorMessage } from '../../utils/apiUtils';
import { normalizeLookupOptions, getSafeLookupOptions, debugLookupOptions } from './TableManager/utils/lookupUtils';
import { scrollToElement } from './TableManager/utils/ScrollUtils';
import useLookupOptions from '../../hooks/useLookupOptions';
import useFetchProperties from '../../hooks/useFetchProperties';
import { SuccessMessage, ErrorMessage } from '../../common/Messages';
import { useToast } from '../../hooks/useToast'; // AGGIUNGI QUESTA RIGA
import Loader from '../../common/Loader';
// Ensure FaCheck is imported if you choose to use it, otherwise FaToggleOn/Off are already there.
import { FaEdit, FaToggleOn, FaToggleOff /*, FaCheck (if using FaCheck) */ } from 'react-icons/fa'; 
import { useNavigate } from 'react-router-dom'; // <<<< IMPORT useNavigate
import { logger } from '../../utils/logger'; // ADD THIS LINE to import logger
import { apiService } from '../../services/apiService';
import RowActionPopup from './TableManager/ui/RowActionPopup'; // AGGIUNTO: Nuovo import

// --- Define Theme Configurations ---
// Define your theme objects here or import them
// Example structure based on how safeTheme was used later
const themeConfigurations = {
    light: {
        // --- CHANGE HERE ---
        themeColor: 'emerald', // Changed from 'blue'
        // --- END CHANGE ---
        success: {
            container: "mb-4 p-3 bg-green-100 text-green-700 rounded-lg flex items-center border-l-4 border-green-500",
            icon: "h-5 w-5 mr-2 flex-shrink-0"
        },
        error: {
            container: "mb-4 p-3 bg-red-100 text-red-700 rounded-lg flex items-center border-l-4 border-red-500",
            icon: "h-5 w-5 mr-2 flex-shrink-0"
        },
    },
    dark: {
        themeColor: 'indigo', // Keep dark theme as indigo for now
        success: {
            container: "mb-4 p-3 bg-green-900/30 text-green-300 rounded-lg flex items-center border-l-4 border-green-600",
            icon: "h-5 w-5 mr-2 flex-shrink-0"
        },
        error: {
            container: "mb-4 p-3 bg-red-900/30 text-red-300 rounded-lg flex items-center border-l-4 border-red-600",
            icon: "h-5 w-5 mr-2 flex-shrink-0"
        },
    },
    // Remove the 'blue' fallback theme if not needed, or update it
    // blue: { ... }
};


// --- Helper function to parse column config string ---
const parseColumnConfigString = (configString) => {
    if (typeof configString !== 'string') {
        console.error("Invalid column config string:", configString);
        return { field: 'UNKNOWN', label: 'Unknown', visibility: 0 };
    }
    const parts = configString.split(',');
    if (parts.length < 2) {
        console.error("Invalid column config string format (must have field=level,label):", configString);
        return { field: 'UNKNOWN', label: 'Unknown', visibility: 0 };
    }

    const fieldPart = parts[0].split('=');
    if (fieldPart.length !== 2) {
        console.error("Invalid column config string format (field part must be FIELD=level):", configString);
        return { field: 'UNKNOWN', label: 'Unknown', visibility: 0 };
    }

    const field = fieldPart[0].trim();
    const visibility = parseInt(fieldPart[1].trim(), 10);
    const label = parts[1].trim();

    const options = {};
    for (let i = 2; i < parts.length; i++) {
        const optionPart = parts[i].split('=');
        if (optionPart.length === 2) {
            options[optionPart[0].trim()] = optionPart[1].trim();
        } else {
            options[optionPart[0].trim()] = true; // For boolean options like 'required'
        }
    }

    return {
        field,
        label,
        visibility,
        ...options
    };
};
// --- End Helper Function ---


const TableManager = ({
  tableName,
  apiEndpoint = '/crud',
  primaryKey,
  columnConfig: initialColumnConfig,
  formColumnConfig,
  isFormFieldVisible,
  filterConfig: initialFilterConfig,
  inputTypes = {},
  lookupConfig = {},
  lookupOptions: propLookupOptions = {},
  initialFilters: propInitialFilters = {},
  autoSearchOnInitialFilters: propAutoSearchOnInitialFilters = false,
  autoSearchOnLoad = true,
  pageTitle = `Gestione ${tableName}`,
  singularRecordName,
  enableCreate = true,
  enableEdit = true,
  enableDelete = true,
  detailViewComponent,
  children,
  renderCustomControls,
  defaultValuesForCreate = {},
  onRowSelectForEdit,
  onNavigateToRelated,
  relatedNavigationPathName,
  defaultSortField = null,
  defaultSortDirection = 'asc',
  statusField = null,
  formLayoutConfig = null,
  autoSelectAndEdit = null,
  hideTable = false,
  // ✅ NUOVO: Aggiungi i callback props per la validazione
  onCreateSuccess,
  onUpdateSuccess,
  onDeleteSuccess,
  onDuplicateStart,
  // ✅ NUOVO: Aggiungi supporto per ordinamento multiplo iniziale
  initialSortBy = null,
  // ✅ NUOVO: Aggiungi supporto per azioni custom nella toolbar
  customActions = [],
  onFetchAllDataRef,
  renderCustomFormActions, // NUOVO: Callback per renderizzare pulsanti custom nel form di modifica
  onFieldChange, // NUOVO: Callback per side-effects al cambio di un campo nel form
  onFiltersChange,
  // ...existing props...
}) => {
  // --- Call ALL Hooks at the Top Level ---
  const contextValue = useTableContext();
  const { theme: currentThemeName } = useTheme();
  const { showSuccess, showError } = useToast();
  
  // ✅ SOLUZIONE DEFINITIVA: Sistema di tracking messaggi con timestamp
  const shownMessagesRef = useRef({ 
    success: { message: null, timestamp: 0 }, 
    error: { message: null, timestamp: 0 } 
  });
  
  const [showNewForm, setShowNewForm] = useState(false);
  const [minimizedForms, setMinimizedForms] = useState({});
  const editFormRef = useRef(null);
  const editFormScrollRef = useRef(null); // Nuovo ref per lo scrolling
  const newFormRef = useRef(null);
  const newFormScrollRef = useRef(null); // Nuovo ref per lo scrolling
  const conflictRef = useRef(null);
  const messageRef = useRef(null);
  const filterPanelRef = useRef(null); // NUOVO: Aggiungi questa ref
  const navigate = useNavigate(); 
  const { properties: fetchedConfig, loading: loadingConfig, error: configError } = useFetchProperties(
      tableName,
      !initialColumnConfig || !initialFilterConfig
  );
  const { lookupOptions: fetchedLookupOptions, loading: loadingLookups = false, error: lookupError } = useLookupOptions(lookupConfig, apiEndpoint) || {};

  // --- Safely Destructure Context AFTER checking ---
  const {
    data = [], loading = false, error = null, setError, success = null, setSuccess, // ✅ AGGIUNGI setSuccess
    filters = propInitialFilters, setFilters, selectedRow = null, setSelectedRow,
    conflictData = null, setConflictData, lookupOptions: contextLookupOptions = {},
    setLookupOptions: setContextLookupOptions, fetchRecords, searchRecords,
    createRecord, updateRecord, deleteRecord,
    pagination: contextPagination = { limit: 10, offset: 0 }, setPagination: setContextPagination,
    sortBy: contextSortBy = [], setSortBy: setContextSortBy,
    // Destructure tableInfo from context, rename it to avoid immediate conflict
    tableInfo: contextTableInfo,
    isProcessingCreate, // Assuming these are from context or local state
    isProcessingUpdate,
    isProcessingDelete,
  } = contextValue || {}; 

  // ✅ SOLUZIONE DEFINITIVA: Gestisce l'oggetto 'success' e resetta lo stato
  useEffect(() => {
    if (success && success.message) {
      showSuccess(success.message);
      // Resetta lo stato nel context a null dopo aver mostrato la notifica.
      // Questo è il modo corretto per preparare il sistema per il prossimo messaggio.
      setSuccess(null); 
    }
  }, [success, showSuccess, setSuccess]);

  useEffect(() => {
    if (error) {
      showError(error);
      // Anche se non è il problema principale, è buona norma resettare anche l'errore.
      setError(null);
    }
  }, [error, showError, setError]);


  // RIMUOVI I VECCHI useEffect che gestivano le notifiche
  /*
  useEffect(() => {
    if (success) {
      const now = Date.now();
      const lastSuccess = shownMessagesRef.current.success;
      
      const shouldShow = !lastSuccess.message || 
                        lastSuccess.message !== success || 
                        (now - lastSuccess.timestamp) > 1000;
      
      if (shouldShow) {
        showSuccess(success);
        shownMessagesRef.current.success = { message: success, timestamp: now };
      } else {

      }
    }
  }, [success, showSuccess]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const lastError = shownMessagesRef.current.error;
      
      const shouldShow = !lastError.message || 
                        lastError.message !== error || 
                        (now - lastError.timestamp) > 1000;
      
      if (shouldShow) {
        showError(error);
        shownMessagesRef.current.error = { message: error, timestamp: now };
      } else {

      }
    }
  }, [error, showError]);

  useEffect(() => {
    if (!success) {
      shownMessagesRef.current.success = { message: null, timestamp: 0 };
    }
  }, [success]);

  useEffect(() => {
    if (!error) {
      shownMessagesRef.current.error = { message: null, timestamp: 0 };
    }
  }, [error]);
  */

  const fetchAllData = useCallback(async (overrideFilters) => {
    // This function should already exist and be correct.
    // It's responsible for fetching all records based on filters.
    // We will use it to get all items for sequencing if needed.
    logger.info(`[TableManager - fetchAllData] Fetching all data for ${tableName} with filters:`, overrideFilters || filters);
    try {
        const response = await searchRecords({ ...overrideFilters, limit: 9999, offset: 0 }); // Use a large limit
        if (response && response.data) {
            return response.data;
        }
        return null;
    } catch (err) {
        logger.error(`[TableManager - fetchAllData] Error fetching all data for ${tableName}`, err);
        setError(err.message || 'Failed to fetch all records for sequencing.');
        return null;
    }
  }, [tableName, searchRecords, filters, setError]);


  const handleSequenceUpdate = useCallback(async (updatedItems, sequenceField, primaryKeyField) => {
    logger.info(`[TableManager] Received ${updatedItems.length} items for sequence update on table ${tableName}.`);
    
    const bulkOperations = updatedItems.map(item => {
      // The backend's simpleUpdateOperation expects both the primary key and the fields to update
      // to be within the 'data' object.
      const primaryKeyValue = item[primaryKeyField];
      const sequenceValue = item[sequenceField];

      return {
        operation: 'update',
        data: {
          [primaryKeyField]: primaryKeyValue,
          [sequenceField]: sequenceValue,
        },
      };
    });

    if (bulkOperations.length === 0) {
        logger.info("[TableManager] No items to update.");
        return true; // Nothing to do
    }

    try {
      // The API endpoint for bulk operations is just /crud, not /crud/:tableName
      // apiService.post returns the response body directly. The backend returns { message, results }.
      const response = await apiService.post(`${apiEndpoint}/${tableName}`, { changes: bulkOperations });
      
      // Check for the presence of the 'results' array to confirm success.
      if (response && response.results) {
        logger.info(`[TableManager] Bulk sequence update successful for ${tableName}.`, response);
        await fetchRecords(); // Refresh data in the table
        return true;
      } else {
        logger.error(`[TableManager] Bulk sequence update failed for ${tableName}. Unexpected response format.`, response);
        const errorMessage = response?.message || response?.error || 'Errore durante l-aggiornamento della sequenza.';
        setError(errorMessage);
        return false;
      }
    } catch (err) {
      logger.error(`[TableManager] Exception during bulk sequence update for ${tableName}.`, err);
      const errorMessage = err.response?.data?.error || err.message || 'Errore di rete o del server.';
      setError(errorMessage);
      return false;
    }
  }, [tableName, apiEndpoint, fetchRecords, setError]);

  const columnProps = useMemo(() => {
    const config = initialColumnConfig || fetchedConfig?.columns || [];
    return config.map(cfg => typeof cfg === 'string' ? parseColumnConfigString(cfg) : cfg)
                   .filter(col => col && col.field); // Ensure field exists
  }, [initialColumnConfig, fetchedConfig]);

  // Consente alle pagine che hanno campi contestuali di mantenere separata la
  // configurazione della griglia da quella dei form. Se la prop non e' fornita,
  // il comportamento rimane esattamente quello storico.
  const formColumnProps = useMemo(() => {
    const config = formColumnConfig || initialColumnConfig || fetchedConfig?.columns || [];
    return config.map(cfg => typeof cfg === 'string' ? parseColumnConfigString(cfg) : cfg)
                 .filter(col => col && col.field);
  }, [formColumnConfig, initialColumnConfig, fetchedConfig]);

  const filterProps = useMemo(() => initialFilterConfig || fetchedConfig?.filters || [], [initialFilterConfig, fetchedConfig]);
  
  const combinedLookupOptions = useMemo(() => ({ 
    ...contextLookupOptions, 
    ...fetchedLookupOptions,
    ...propLookupOptions // <<< AGGIUNGI LE PROP LOOKUP OPTIONS
  }), [contextLookupOptions, fetchedLookupOptions, propLookupOptions]);
    
  const requiredLookupFields = useMemo(() => {
    const allColumnProps = [...columnProps, ...formColumnProps];
    return allColumnProps
      .filter(col => col && (col.lookupField || (inputTypes && inputTypes[col.field] === 'select')))
      .map(col => col.field);
  }, [columnProps, formColumnProps, inputTypes]);
  
  const safeLookupOptions = useMemo(() => normalizeLookupOptions(combinedLookupOptions, requiredLookupFields), [combinedLookupOptions, requiredLookupFields]);
  
  const safeTheme = useMemo(() => {
    const baseTheme = themeConfigurations[currentThemeName] || themeConfigurations.light;
    // If a specific themeColor is passed as a prop, it overrides the theme's default
    // This part was commented out, ensure it's handled if themeColor prop is used
    // return initialThemeColor ? { ...baseTheme, themeColor: initialThemeColor } : baseTheme;
    return baseTheme;
  }, [currentThemeName/*, initialThemeColor*/]);


  const tableInfo = useMemo(() => contextTableInfo || { totalRecords: 0, totalPages: 0 }, [contextTableInfo]);

  const [tablePagination, setTablePagination] = useState(() => ({
    pageIndex: contextPagination.offset / contextPagination.limit,
    pageSize: contextPagination.limit,
  }));

  const { pageIndex, pageSize } = tablePagination;

  // ✅ MODIFICATO: Inizializza sorting con initialSortBy se fornito
  const [sorting, setTableSorting] = useState(() => {
    // Se abbiamo initialSortBy, usalo
    if (initialSortBy && Array.isArray(initialSortBy)) {
      return initialSortBy;
    }
    // Altrimenti usa contextSortBy o costruisci da defaultSortField
    if (contextSortBy && contextSortBy.length > 0) {
      return contextSortBy;
    }
    // Fallback: usa defaultSortField e defaultSortDirection
    if (defaultSortField) {
      return [{ id: defaultSortField, desc: defaultSortDirection === 'desc' }];
    }
    return [];
  }); 

  const [clickTimeoutId, setClickTimeoutId] = useState(null);
  const [lastClickedRowId, setLastClickedRowId] = useState(null);
  const [detailViewData, setDetailViewData] = useState(null); 
  const [detailViewPosition, setDetailViewPosition] = useState(null); 
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false); 
  const DOUBLE_CLICK_DELAY = 300; // milliseconds
  // Add this state around line 307 (after the other state declarations)

  const [duplicateData, setDuplicateData] = useState(null); // NUOVO: Stato per dati duplicati

  // --- Effects ---

  // This effect runs when the component mounts or when critical props like initialFilters change.
  // It's the key to ensuring the correct data is loaded.
  useEffect(() => {
    logger.debug(`[TableManagerInner ${tableName}] Initializing...`, { 
      hasInitialFilters: propInitialFilters && Object.keys(propInitialFilters).length > 0,
      autoSearchOnInitialFilters: propAutoSearchOnInitialFilters,
      autoSearchOnLoad: autoSearchOnLoad
    });

    // If we have initial filters and are supposed to search with them, do it now.
    // This is the primary path for navigation with filters (including new tabs).
    if (propAutoSearchOnInitialFilters && propInitialFilters && Object.keys(propInitialFilters).length > 0) {
      logger.info(`[TableManagerInner ${tableName}] Triggering search with initial filters:`, propInitialFilters);
      // We call searchRecords directly with the initial filters to ensure they are applied.
      // We also reset pagination to the first page.
      searchRecords({ 
        filters: propInitialFilters, 
        limit: pageSize, 
        offset: 0, 
        sortBy: sorting 
      });
    } 
    // Otherwise, if there are no initial filters but we should load data on mount, do that.
    else if (autoSearchOnLoad) {
      // Triggering initial empty search
      // We use an empty filter object here to fetch the initial, unfiltered dataset.
      searchRecords({ 
        filters: {}, 
        limit: pageSize, 
        offset: 0, 
        sortBy: sorting 
      });
    }
    // If neither of the above is true, we don't fetch data on mount and wait for user interaction.

  // We only want this effect to run when the component is first mounted or re-mounted (due to key change).
  // Adding dependencies would cause it to re-run on every filter change, which is handled elsewhere.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // The empty dependency array is intentional and correct here.


  // --- Callbacks ---

  // Function to handle the actual selection for editing (double-click action)
  const handleRowEditSelect = useCallback((row) => {
    if (!setSelectedRow || !setFilters) return;
    const newRowId = row?.[primaryKey];
    const previousRowId = selectedRow?.[primaryKey];

    // SEMPRE chiudi il detail view quando si fa doppio click (indipendentemente dal row)
    if (isDetailViewOpen) {
        setIsDetailViewOpen(false);
        setDetailViewData(null);
        setDetailViewPosition(null);
    }

    if (newRowId) {
        // Minimize the PREVIOUSLY selected form IF it's different
        if (previousRowId && previousRowId !== newRowId && minimizedForms[previousRowId] === false) {
            setMinimizedForms(prev => ({ ...prev, [previousRowId]: true }));
        }
        // Set the new selected row and ensure its form is NOT minimized
        setSelectedRow(row);
        setShowNewForm(false);
        setMinimizedForms(prev => ({ ...prev, [newRowId]: false }));
    } else {
        // If row is null/invalid, deselect
        if (previousRowId) {
             setMinimizedForms(prev => ({ ...prev, [previousRowId]: true }));
        }
        setSelectedRow(null);
    }
}, [setSelectedRow, primaryKey, selectedRow, minimizedForms, setMinimizedForms, isDetailViewOpen, setIsDetailViewOpen, setDetailViewData, setDetailViewPosition, setShowNewForm, setFilters]);

  // handleRowViewSelect (Single-click action) - Updated for inline view
  const handleRowViewSelect = useCallback((row, position) => {
      const clickedRowId = row?.[primaryKey];
      console.log('[TableManager] handleRowViewSelect called with row:', clickedRowId);
      if (!clickedRowId) {
          return;
      }

      // If detail view is already open for THIS row, close it
      if (isDetailViewOpen && detailViewData && detailViewData[primaryKey] === clickedRowId) {
          console.log('[TableManager] Closing detail view for same row');
          setIsDetailViewOpen(false);
          setDetailViewData(null);
          setDetailViewPosition(null);
      } else {
          // Otherwise, open it (or move it if already open for another row)
          console.log('[TableManager] Opening detail view with position:', position);
          setDetailViewData(row);
          setDetailViewPosition(position);
          setIsDetailViewOpen(true);

          // REMOVED: Non minimizzare automaticamente i form esistenti
          // Questo permette di mantenere aperte le macrofunzioni
      }

  }, [isDetailViewOpen, detailViewData, primaryKey]); // Rimossi setMinimizedForms, selectedRow, setSelectedRow dalle dependencies

  // handleRowClick - MODIFICATO: Ora gestisce solo il tasto destro per aprire il popup
  // Il doppio click per editare è gestito separatamente
  const handleRowClick = useCallback((row, position) => {
      const clickedRowId = row?.[primaryKey];
      console.log('[TableManager] handleRowClick called with row:', clickedRowId, 'position:', position);
      if (!clickedRowId) return;
      
      // Apri direttamente il popup (ora triggerato dal context menu)
      handleRowViewSelect(row, position);
  }, [primaryKey, handleRowViewSelect]);

  // Callback to close the inline detail view
  const handleCloseDetailView = useCallback(() => {
      setIsDetailViewOpen(false);
      setDetailViewData(null);
      setDetailViewPosition(null);
  }, []);


  // --- Remove the OLD handleRowSelect definition ---
  /*
  const handleRowSelect = useCallback((row) => {
      // ... old logic ...
  }, [setSelectedRow, primaryKey, selectedRow, minimizedForms, setMinimizedForms]);
  */

  // --- ADDED DEFINITION - Move this BEFORE columns useMemo ---
  const handleBooleanToggle = useCallback(async (row, columnId, newValue) => {
      // Ensure row.original exists and contains the primary key
      const recordId = row?.original?.[primaryKey];

      // Ensure context functions are available
      if (!updateRecord || !primaryKey) {
          // console.error("[TableManager] updateRecord function or primaryKey is missing, cannot toggle boolean.");
          if (setError) setError("Errore interno: impossibile aggiornare il valore.");
          return;
      }

      if (recordId === undefined || recordId === null) {
          // console.error("[TableManager] Primary key not found in row data:", row);
          if (setError) setError("Errore interno: ID record non trovato.");
          return;
      }

      const payload = {
          [primaryKey]: recordId,
          [columnId]: newValue,
      };

      // console.log(`[TableManager] Toggling boolean for ${columnId} to ${newValue} for ID ${recordId}`);
      // Call the updateRecord function from context
      await updateRecord(payload);
      // Optionally trigger a re-fetch or rely on context update propagation
      // handleSearch(); // Uncomment if you want to force a refresh after toggle

  }, [updateRecord, primaryKey, setError]); // Add dependencies: updateRecord, primaryKey, setError
  // --- END ADD DEFINITION ---

  // --- DEFINE COLUMNS HERE ---
  const columns = useMemo(() => {
    if (!columnProps || columnProps.length === 0) {
        // logger.warn(`[TableManagerInner ${tableName}] columnProps is empty or undefined. Returning empty columns array.`);
        return [];
    }
    // logger.debug(`[TableManagerInner ${tableName}] Generating columns. columnProps count: ${columnProps.length}`);

    return columnProps
      .filter(col => {
        const shouldShow = col.visibility === 2; // Only show columns with visibility 2 in table
        return shouldShow;
      })
      .map((col) => {
        const columnDef = {
          accessorKey: col.field,
          header: col.label || col.field,
          id: col.field, // Ensure id is set, defaults to accessorKey if not provided
          enableSorting: true, // Default to true, can be overridden by col.enableSorting === false
          meta: col.maxWidth ? { maxWidth: parseInt(col.maxWidth, 10) } : undefined,
        };

        if (col.enableSorting === false) {
            columnDef.enableSorting = false;
        }
        
        // Handle boolean columns with toggle functionality
        if (col.isBoolean) {
          columnDef.cell = ({ row, getValue }) => {
            const value = getValue();
            // More robust check for boolean true
            const isTrue = value === true || value === 1 || String(value).toLowerCase() === '1' || String(value).toLowerCase() === 'true';
            
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click event
                  handleBooleanToggle(row, col.field, !isTrue);
                }}
                className={`p-1 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 dark:focus:ring-offset-gray-800 focus:ring-${safeTheme.themeColor}-500`}
                title={`${col.label || col.field}: ${isTrue ? 'Attivo' : 'Inattivo'} - Clicca per cambiare`}
              >
                {isTrue 
                  ? <FaToggleOn size={20} className={`text-${safeTheme.themeColor}-600 dark:text-${safeTheme.themeColor}-400 hover:text-${safeTheme.themeColor}-700 dark:hover:text-${safeTheme.themeColor}-300`} /> 
                  : <FaToggleOff size={20} className={`text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400`} />}
              </button>
            );
          };
        }
        // Handle lookup columns
        else if (col.lookupName && safeLookupOptions[col.lookupName]) {
          columnDef.cell = ({ getValue }) => {
            const value = getValue();
            const options = safeLookupOptions[col.lookupName] || [];
            const lookupOption = options.find(
              option => String(option.value) === String(value) // Robust comparison
            );
            // logger.trace(`[TableManagerInner ${tableName}] Lookup for ${col.field}: value=${value}, option=${lookupOption?.label}`);
            return lookupOption?.label || value || ''; // Fallback to value if not found
          };
        } else if (col.lookupName) {
            // logger.warn(`[TableManagerInner ${tableName}] Lookup options for ${col.lookupName} (field ${col.field}) not found or empty.`);
            columnDef.cell = ({ getValue }) => getValue() ?? ''; // Render value or empty string
        }
        // Default cell renderer (TanStack Table handles this, but explicit for clarity)
        // else {
        //   columnDef.cell = ({ getValue }) => getValue() ?? '';
        // }

        return columnDef;
      });
  }, [columnProps, safeLookupOptions, safeTheme.themeColor, handleBooleanToggle]); // Removed tableName dependency

  // --- Effects (depend on state/context/props) ---
  useEffect(() => { if (lookupError) console.error("Errore caricamento lookup:", lookupError); }, [lookupError]);

  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [filters, onFiltersChange]);

  // AGGIUNTO: useEffect per gestire l'autoSelectAndEdit
  useEffect(() => {
    if (autoSelectAndEdit && data && data.length > 0 && !selectedRow) {

      const recordToEdit = data.find(record => String(record[primaryKey]) === String(autoSelectAndEdit));
      
      if (recordToEdit) {

        setSelectedRow(recordToEdit);
        setMinimizedForms(prev => ({ ...prev, [recordToEdit[primaryKey]]: false }));
        setShowNewForm(false);
      } else {
        console.warn('[TableManager] Record non trovato per autoSelectAndEdit ID:', autoSelectAndEdit);
      }
    }
  }, [autoSelectAndEdit, data, selectedRow, primaryKey, setSelectedRow, setMinimizedForms]);

  // --- FIX: Use useRef to track previous values and avoid infinite loops ---
  const previousLookupOptionsRef = useRef();
  const previousPaginationRef = useRef();
  const previousSortingRef = useRef();

  // --- FIX 1: Fix the lookup options effect ---
  useEffect(() => {
    const safeLookupOptionsString = JSON.stringify(safeLookupOptions);
    const previousLookupOptionsString = previousLookupOptionsRef.current;

    if (safeLookupOptionsString !== previousLookupOptionsString) {
        // console.log("[TableManager] Lookup options changed, updating context.");
        setContextLookupOptions(safeLookupOptions);
        previousLookupOptionsRef.current = safeLookupOptionsString;
    }
  }, [safeLookupOptions, setContextLookupOptions]); // REMOVED contextLookupOptions from dependencies

  // --- FIX 2: Fix pagination effect ---
  useEffect(() => {
      const newLimit = contextPagination.limit || 10;
      const newOffset = contextPagination.offset ?? 0;
      const newPageIndex = Math.floor(newOffset / newLimit);

      const currentPagination = { limit: newLimit, offset: newOffset, pageIndex: newPageIndex };
      const currentPaginationString = JSON.stringify(currentPagination);
      const previousPaginationString = previousPaginationRef.current;

      if (currentPaginationString !== previousPaginationString) {
          // console.log(`Syncing context pagination to local: Index ${pageIndex} -> ${newPageIndex}, Size ${pageSize} -> ${newLimit}`);
          setTablePagination({
              pageIndex: newPageIndex,
              pageSize: newLimit,
          });
          previousPaginationRef.current = currentPaginationString;
      }
  }, [contextPagination.limit, contextPagination.offset]); // REMOVED pageIndex, pageSize from dependencies

  // --- FIX 3: Fix sorting effect ---
  useEffect(() => {
      const newSortString = JSON.stringify(contextSortBy || []);
      const previousSortString = previousSortingRef.current;
      
      if (newSortString !== previousSortString) {
        //  console.log('Syncing context sorting to local:', contextSortBy);
         setTableSorting(contextSortBy || []);
         previousSortingRef.current = newSortString;
      }
  }, [contextSortBy]); // REMOVED sorting from dependencies

  // Simple scrolling effects
  useEffect(() => {
    if (selectedRow && editFormScrollRef.current) {
      setTimeout(() => {
        try {
          editFormScrollRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start'
          });
        } catch (error) {
          // Ignore scroll errors to avoid side effects
        }
      }, 200);
    }
  }, [selectedRow, primaryKey]);

  useEffect(() => { 
    if (showNewForm && newFormScrollRef.current) {
      setTimeout(() => {
        try {
          newFormScrollRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start'
          });
        } catch (error) {
          // Ignore scroll errors to avoid side effects
        }
      }, 200);
    }
  }, [showNewForm]);
  useEffect(() => { if (conflictData) scrollToElement(conflictRef.current); }, [conflictData]);
  useEffect(() => { if (error || success) scrollToElement(messageRef.current); }, [error, success]);



  // --- ADD LOG BEFORE useReactTable ---
  // console.log("[TableManager] Data being passed to useReactTable:", data);
  // --- END LOG ---

  // --- TanStack Table Instance ---
  const table = useReactTable({
    data: data || [],
    columns,
    state: {
        pagination: { pageIndex, pageSize },
        sorting: sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: tableInfo?.totalPages ?? -1,
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater(tablePagination) : updater;
      setTablePagination(newPagination);
      setContextPagination({
        limit: newPagination.pageSize,
        offset: newPagination.pageIndex * newPagination.pageSize,
      });
      // Passa i filtri correnti durante il cambio pagina
      fetchRecords({
        limit: newPagination.pageSize,
        offset: newPagination.pageIndex * newPagination.pageSize,
        sortBy: sorting,
        filters: filters, // Usa i filtri attuali
      });
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setTableSorting(newSorting);
      setContextSortBy(newSorting);
      // Passa i filtri correnti durante l'ordinamento
      fetchRecords({
        limit: pageSize,
        offset: 0, // Torna alla prima pagina quando si ordina
        sortBy: newSorting,
        filters: filters, // Usa i filtri attuali
      });
    },
    debugTable: false, // Disabled debug logs
  });

  // --- Other Callbacks ---
  const handleFilterChange = useCallback((field, value) => {
    // Filter change debug removed
    
    setFilters(currentFilters => {
      const newFilters = { ...currentFilters };

      if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
        delete newFilters[field];
        // Se è un campo di testo, rimuovi anche il filtro _like associato
        if (!field.endsWith('_like')) {
          delete newFilters[`${field}_like`];
        }
        // FIX: Also remove the corresponding regular field if this is a _like field
        if (field.endsWith('_like')) {
          const baseField = field.replace('_like', '');
          delete newFilters[baseField];
        }
      } else {
        // Set the field value normally
        newFilters[field] = value;
      }
      
      logger.debug(`[TableManagerInner ${tableName}] setFilters (CONTEXT) callback - Computed newFilters for context:`, JSON.stringify(newFilters), 'Raw newFilters object:', newFilters);
      
      return newFilters; // This updates the context's filter state
    });
  }, [setFilters, tableName]);

  // **CORREZIONE CRITICA**: handleSearch ora accetta filtri opzionali
  const handleSearch = useCallback((overrideFilters = null) => {
    // NUOVA LOGICA: Unisce l'aggiornamento e la ricerca
    if (!filterPanelRef.current) {
      // Se non c'è filterPanel, usa i filtri passati o quelli correnti
      const filtersToUse = overrideFilters || filters;

      
      searchRecords({
        filters: filtersToUse,
        limit: pageSize,
        sortBy: sorting,
      });
      return;
    }
    
    // Ottieni i filtri di testo locali dal GenericFilterPanel
    const localTextFilters = filterPanelRef.current.getLocalTextFilters();

    
    // Combina i filtri globali con quelli di testo locali
    const baseFilters = overrideFilters || filters;
    const combinedFilters = { ...baseFilters };
    
    // Applica i filtri di testo locali
    Object.entries(localTextFilters).forEach(([field, value]) => {
      if (value && value.trim()) {
        combinedFilters[`${field}_like`] = value.trim();
        // Rimuovi il filtro esatto se presente
        delete combinedFilters[field];
      } else {
        // Se il valore è vuoto, rimuovi entrambi i filtri
        delete combinedFilters[field];
        delete combinedFilters[`${field}_like`];
      }
    });
    

    
    searchRecords({
      filters: combinedFilters,
      limit: pageSize,
      sortBy: sorting,
    });
  }, [filterPanelRef, filters, pageSize, sorting, searchRecords, tableName]);

  const handleResetFilters = useCallback(() => {
    setFilters(propInitialFilters);
    setContextPagination(prev => ({ ...prev, offset: 0 }));
    setSelectedRow(null);
    setMinimizedForms({});
    // Fetch records with the reset filters, ensuring pagination is also reset
    searchRecords({ filters: propInitialFilters, limit: pageSize, offset: 0, sortBy: sorting });
  }, [propInitialFilters, pageSize, sorting, searchRecords, setFilters, setContextPagination, setSelectedRow, tableName]); // Changed fetchRecords to searchRecords

  const handleCancelEdit = useCallback(() => {
      if (!setSelectedRow || !setConflictData) return;
      setSelectedRow(null);
      setConflictData(null);
  }, [setSelectedRow, setConflictData]);

  const handleCancelNew = useCallback(() => {
      setShowNewForm(false);
      setDuplicateData(null); // NUOVO: Reset dati duplicati
  }, []);

    const handleCreateClick = useCallback(() => {
      if (!setSelectedRow) return;
      setShowNewForm(true);
      setSelectedRow(null);
      setMinimizedForms({});
      setDuplicateData(null); // NUOVO: Reset dati duplicati quando si crea un nuovo record
  }, [setSelectedRow]);

const handleCreateSubmit = useCallback(
  async (newRecordData, action = null) => {

    
    const created = await createRecord(newRecordData);
    if (created && created[primaryKey]) {
      setShowNewForm(false);
      setDuplicateData(null);
      
      // Handle switchToEdit action
      if (action === 'switchToEdit') {
        setSelectedRow(created);
        setMinimizedForms(prev => ({ ...prev, [created[primaryKey]]: false }));
      }
      
      // ✅ NUOVO: Chiama il callback onCreateSuccess se fornito
      if (onCreateSuccess) {

        onCreateSuccess(created);
      }
      
      return created;
    }
    return null;
  },
  [createRecord, primaryKey, setSelectedRow, setMinimizedForms, onCreateSuccess]
);

  const handleUpdateSubmit = useCallback(async (updatedRecordData) => {
    if (!updateRecord) return;
    const success = await updateRecord(updatedRecordData);
    if (success) {
      handleSearch();
      
      // ✅ NUOVO: Chiama il callback onUpdateSuccess se fornito
      if (onUpdateSuccess) {

        onUpdateSuccess(updatedRecordData);
      }
    }
  }, [updateRecord, handleSearch, onUpdateSuccess]);

  const handleDeleteSubmit = useCallback(async () => {
      if (!selectedRow || !deleteRecord || !setSelectedRow) return;
      const success = await deleteRecord(selectedRow[primaryKey]);
      if (success) {
          setSelectedRow(null);
          handleSearch();
      }
  }, [deleteRecord, selectedRow, primaryKey, setSelectedRow, handleSearch]);

  const handleAcceptConflictCurrent = useCallback(() => {
      if (!setConflictData || !setSelectedRow) return;
      setConflictData(null);
      setSelectedRow(null);
  }, [setConflictData, setSelectedRow]);

  const handleMergeConflict = useCallback(async () => {
      if (!conflictData || !updateRecord || !setConflictData) return;
      const mergePayload = {
          ...conflictData.yourChanges,
          [primaryKey]: conflictData.recordId,
      };
      const success = await updateRecord(mergePayload);
      if (success) {
          setConflictData(null);
          handleSearch();
      }
  }, [conflictData, updateRecord, primaryKey, setConflictData, handleSearch]);

  const handleCancelConflict = useCallback(() => {
       if (!setConflictData) return;
       setConflictData(null);
   }, [setConflictData]);

  const handleMinimizeToggle = useCallback((recordId) => {
      if (!setSelectedRow) return;
      setMinimizedForms(prev => ({
          ...prev,
          [recordId]: !prev[recordId]
      }));
      if (selectedRow && String(selectedRow[primaryKey]) === String(recordId) && !minimizedForms[recordId]) {
          setSelectedRow(null);
      }
   }, [selectedRow, primaryKey, setSelectedRow, minimizedForms]);

  const handleRestoreForm = useCallback((record) => {
       if (!setSelectedRow) return;
       setSelectedRow(record);
       setMinimizedForms(prev => ({ ...prev, [record[primaryKey]]: false }));
       setShowNewForm(false);
   }, [setSelectedRow, primaryKey]);

  // Add missing callback functions for RowActionPopup
  const handleDuplicateRecord = useCallback(async (row) => {
    logger.info(`[TableManager] Duplicating record from table ${tableName}:`, row);
    
    // ✅ NUOVO: Chiama il callback onDuplicateStart se fornito
    if (onDuplicateStart) {
      logger.info('[TableManager] Calling onDuplicateStart callback');
      onDuplicateStart(row);
    }
    
    // Create a copy of the row data, excluding the primary key
    const { [primaryKey]: _, ...rowDataWithoutPK } = row;
    
    // ✅ CORREZIONE: Rimuovi TUTTI i campi che non devono essere duplicati
    // Questo include: primary key, campi IdRif (versionamento), campi tecnici, MAC_Id_Tabelle
    let dataForDuplication = rowDataWithoutPK;
    
    // Lista di pattern per campi da escludere dalla duplicazione
    const fieldsToExclude = [
      // Campi di versionamento (IdRif)
      /^[A-Z]{3}_IdRif_/i,
      // Campi tecnici di audit - Inserimento
      /^[A-Z]{3}_UtenteInserimento$/i,
      /^[A-Z]{3}_DataOraInserimento$/i,
      /^[A-Z]{3}_TerminaleInserimento$/i,
      // Campi tecnici di audit - Modifica
      /^[A-Z]{3}_UtenteModifica$/i,
      /^[A-Z]{3}_DataOraModifica$/i,
      /^[A-Z]{3}_TerminaleModifica$/i,
      // Campi di stato e validità
      /^[A-Z]{3}_StatiRecord$/i,
      /^[A-Z]{3}_InizioValidita$/i,
      /^[A-Z]{3}_FineValidita$/i,
      /^[A-Z]{3}_Timestamp$/i,
    ];
    
    // Filtra i campi che matchano i pattern da escludere
    dataForDuplication = Object.fromEntries(
      Object.entries(rowDataWithoutPK).filter(([key, value]) => {
        const shouldExclude = fieldsToExclude.some(pattern => pattern.test(key));
        if (shouldExclude) {
          logger.debug(`[TableManager] Excluding field from duplication: ${key}`);
        }
        return !shouldExclude;
      })
    );
    
    // Gestione specifica per SKE_MacroFunzioni: esclude anche MAC_Id_Tabelle
    if (tableName === 'SKE_MacroFunzioni') {
      const { MAC_Id_Tabelle, ...dataWithoutTableRef } = dataForDuplication;
      dataForDuplication = dataWithoutTableRef;
      logger.info('[TableManager] MacroFunzioni: Removed MAC_Id_Tabelle from duplication data');
    }
    
    logger.info('[TableManager] Data prepared for duplication (cleaned):', dataForDuplication);

    
    try {
      // Close the detail view
      setIsDetailViewOpen(false);
      setDetailViewData(null);
      setDetailViewPosition(null);
      
      // Close any existing edit forms
      if (selectedRow) {
        setSelectedRow(null);
      }
      setMinimizedForms({});
      
      // Set the duplicate data and open the new record form
      setDuplicateData(dataForDuplication);
      setShowNewForm(true);
      
      logger.info('[TableManager] New record form opened with duplicate data');

      
    } catch (error) {
      console.error('[TableManager] Error preparing duplicate record:', error);
      if (setError) {
        setError('Errore durante la preparazione della duplicazione del record');
      }
    }
  }, [primaryKey, tableName, setIsDetailViewOpen, setDetailViewData, setDetailViewPosition, selectedRow, setSelectedRow, setMinimizedForms, setShowNewForm, setError, onDuplicateStart]);

  const handleDeleteFromPopup = useCallback(async (row) => {
    if (!deleteRecord || !row) return;
    
    const recordId = row[primaryKey];
    if (!recordId) return;
    
    // Show confirmation dialog
    const confirmDelete = window.confirm(
      `Sei sicuro di voler eliminare questo record? Questa operazione non può essere annullata.`
    );
    
    if (!confirmDelete) return;
    
    try {
      // Close the detail view first
      setIsDetailViewOpen(false);
      setDetailViewData(null);
      setDetailViewPosition(null);
      
      // Delete the record
      const success = await deleteRecord(recordId);
      
      if (success) {
        // Clear selection if this record was selected
        if (selectedRow && selectedRow[primaryKey] === recordId) {
          setSelectedRow(null);
        }
        
        // Refresh the data
        handleSearch();
      }
    } catch (error) {
      console.error('[TableManager] Error deleting record from popup:', error);
      if (setError) {
        setError('Errore durante l\'eliminazione del record');
      }
    }
  }, [deleteRecord, primaryKey, setIsDetailViewOpen, setDetailViewData, setDetailViewPosition, selectedRow, setSelectedRow, handleSearch, setError]);

  // --- Prepare props for EditRecordForm's "View Related" button ---
  // This section correctly prepares the generic handlers
  const relatedItemsHandler = useMemo(() => {
    if (onNavigateToRelated && selectedRow) {
      return () => { // This inner function is what EditRecordForm calls
        const pkValue = selectedRow[primaryKey];
        let secondaryIdValue;

        // Determine the secondary ID based on the table, if needed by onNavigateToRelated
        if (tableName === 'SKE_Tabelle') {
          secondaryIdValue = selectedRow.TAB_Id_Moduli; 
        } else if (tableName === 'SKE_Oggetti') {
          secondaryIdValue = selectedRow.OGG_Id_Moduli;
        }
        // Add more conditions if other tables need this feature

        if (pkValue !== undefined) {
          // Call the navigation function passed from the parent page
          // The parent's navigation function (onNavigateToRelated) is responsible for using these values
          onNavigateToRelated(navigate, pkValue, secondaryIdValue);
        } else {
          // logger.warn(`[TableManager] Missing primaryKey value ('${primaryKey}') in selectedRow for related navigation. Table: ${tableName}`, selectedRow);
        }
      };
    }
    return undefined;
  }, [onNavigateToRelated, selectedRow, primaryKey, tableName, navigate]);

  const relatedButtonText = useMemo(() => {
    return relatedNavigationPathName ? `Visualizza ${relatedNavigationPathName}` : undefined;
  }, [relatedNavigationPathName]);

  // ✅ NUOVO: Esponi fetchAllData se richiesto
  useEffect(() => {
    if (onFetchAllDataRef && typeof onFetchAllDataRef === 'function') {
      onFetchAllDataRef(fetchAllData);
    }
  }, [onFetchAllDataRef, fetchAllData]);

  // --- Conditional Return for Loading/Missing Context ---
  // Check if context is truly missing AFTER hooks have been called
  if (!contextValue) {
    // console.error("TableContext is undefined. Ensure TableManagerInner is wrapped by TableProvider.");
    return (
        <div className="p-4 md:p-6 flex justify-center items-center min-h-[300px]">
            <Loader />
        </div>
    );
  }

  // --- Loading State ---
  const isOverallLoading = loading || loadingLookups || loadingConfig;

  // --- Render Logic ---
  // console.log("[TableManager] Rendering. selectedRow:", selectedRow, "minimizedForms:", minimizedForms, "enableEdit:", enableEdit);
  // Filter panel rendering debug removed
  return (
    <div className={`p-4 md:p-6 relative bg-emerald-50/60 dark:bg-gray-950 transition-colors duration-300`}>
      {/* --- CHANGE: Ensure light text in dark mode --- */}
      <h2 className={`text-2xl font-bold mb-4 text-${safeTheme.themeColor}-700 dark:text-${safeTheme.themeColor}-200`}>{pageTitle}</h2>

      {/* Render children passed to TableManager, e.g., CustomActionButtons */}
      {children}

      {/* Messages - COMMENTATI per usare toast */}
      {/* 
      <div ref={messageRef}>
        <SuccessMessage message={success} theme={safeTheme} />
        <ErrorMessage message={error} theme={safeTheme} />
      </div>
      */}

      {/* Filter Panel - Renderizza solo se filterProps è fornito e non vuoto */}
      {filterProps && filterProps.length > 0 && (
        <div className={`
          mb-4 rounded-xl shadow border border-${safeTheme.themeColor}-300 dark:border-${safeTheme.themeColor}-700
          bg-white dark:bg-gray-800 overflow-hidden
        `}>
          {/* Filter Header - CORRETTO per tema scuro */}
          <div className={`
            px-4 py-2 font-bold text-${safeTheme.themeColor}-800 dark:text-white
            bg-${safeTheme.themeColor}-100 dark:bg-gray-900
            border-b border-${safeTheme.themeColor}-300 dark:border-gray-700
            text-base tracking-wide
          `}>
            Filtri
          </div>
          {/* Filter Content */}
          <div className="p-4">
            <GenericFilterPanel
              ref={filterPanelRef} // NUOVO: Assegna la ref
              filterConfig={filterProps}
              filters={filters}
              onFilterChange={handleFilterChange}
              onSearch={handleSearch}
              onReset={handleResetFilters}
              theme={safeTheme}
              lookupOptions={safeLookupOptions}
              isLoadingLookups={loadingLookups}
            />
          </div>
        </div>
      )}

      {/* Area Custom Actions */}
      {customActions && customActions.length > 0 && (
        <div className="my-4 flex flex-wrap gap-2">
          {customActions.map((action, index) => {
            const isDisabled = action.requiresSelection && (!selectedRow || Object.keys(selectedRow).length === 0);
            const IconComponent = action.icon;
            const variant = action.variant || safeTheme.themeColor;
            
            return (
              <button
                key={index}
                onClick={() => action.onClick(selectedRow ? [selectedRow] : [])}
                disabled={isDisabled}
                title={action.tooltip || action.label}
                className={`
                  px-4 py-2 rounded transition flex items-center gap-2
                  ${isDisabled 
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed' 
                    : `bg-${variant}-600 hover:bg-${variant}-700 text-white`
                  }
                `}
              >
                {IconComponent && <IconComponent className="w-4 h-4" />}
                {action.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Area Controlli Custom */}
      {renderCustomControls && renderCustomControls({ 
        currentFilters: filters, 
        fetchAllData, 
        fetchRecords, 
        updateSequence: handleSequenceUpdate 
      })}

      {/* Create Button */}
      {enableCreate && !showNewForm && (
          <div className="my-4 text-right">
              <button
                  onClick={handleCreateClick}
                  className={`px-4 py-2 bg-${safeTheme.themeColor}-600 text-white rounded hover:bg-${safeTheme.themeColor}-700 transition`}
              >
                  + Aggiungi Nuovo
              </button>
          </div>
      )}

      {/* New Record Form OR Custom Detail View for Creation */}
      {showNewForm && enableCreate && (
        <div ref={newFormScrollRef} className="scroll-target-new-form">
          {detailViewComponent ? (
            React.createElement(detailViewComponent, {
              ref: newFormRef,
              key: 'create-new-record',
              isCreating: true,
              initialDataForCreate: duplicateData || defaultValuesForCreate, // MODIFICATO: Usa dati duplicati se disponibili
              onCreate: handleCreateSubmit,
              onCancel: handleCancelNew,
              columnProps: formColumnProps,
              inputTypes,
              lookupOptions: safeLookupOptions,
              isLoadingLookups: loadingLookups,
              theme: safeTheme,
              primaryKey,
              isProcessing: loading,
              formLayoutConfig,
              isFieldVisible: isFormFieldVisible,
            })
          ) : (
            <NewRecordForm
                ref={newFormRef}
                onCreate={handleCreateSubmit}
                onCancel={handleCancelNew}
                columnProps={formColumnProps}
                inputTypes={inputTypes}
                lookupOptions={safeLookupOptions}
                isLoadingLookups={loadingLookups}
                theme={safeTheme}
                isProcessing={loading}
                initialData={duplicateData || defaultValuesForCreate} // MODIFICATO: Usa dati duplicati se disponibili
                formLayoutConfig={formLayoutConfig}
                onFieldChange={onFieldChange}
                isFieldVisible={isFormFieldVisible}
            />
          )}
        </div>
      )}

      {/* Data Table - Pass the statusField prop */}
      {!hideTable && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <DataTable
              table={table}
              loading={isOverallLoading}
              onRowSelect={handleRowClick}
              onRowDoubleClick={handleRowEditSelect}
              primaryKey={primaryKey}
              selectedRow={selectedRow}
              theme={safeTheme}
              statusField={statusField} // Pass the new statusField prop
              lookupOptions={safeLookupOptions} // AGGIUNTO: Pass the lookup options
          />

          {/* Pagination - Active button styling is handled within Pagination component, ensure it uses theme */}
          {/* Assuming Pagination component uses theme prop for active state like: */}
          {/* className={`... ${isActive ? `bg-${theme.themeColor}-700 text-white ring-2 ring-offset-1 ring-${theme.themeColor}-500` : '...'}`} */}
          <Pagination
              table={table}
              dataLength={tableInfo?.totalRecords ?? 0}
              theme={safeTheme} // Pass theme to Pagination
          />
        </div>
      )}

      {/* Conflict Resolution */}
      <ConflictResolution
          ref={conflictRef}
          conflictData={conflictData}
          onAcceptCurrent={handleAcceptConflictCurrent}
          onMerge={handleMergeConflict}
          onCancel={handleCancelConflict}
          theme={safeTheme}
          columnProps={columnProps}
          primaryKey={primaryKey}
      />

      {/* Edit Record Form OR Custom Detail View */}
      {selectedRow && !minimizedForms[selectedRow[primaryKey]] && enableEdit && (
        <div ref={editFormScrollRef} className="scroll-target-edit-form">
          {detailViewComponent ? // MODIFIED: Prioritize detailViewComponent if provided
            React.createElement(detailViewComponent, {
              ref: editFormRef, 
              key: selectedRow[primaryKey], 
              isCreating: false, // Explicitly set to false for edit mode
              selectedRow,
              onUpdate: handleUpdateSubmit,
              onCancel: handleCancelEdit,
              columnProps: formColumnProps,
              inputTypes,
              lookupOptions: safeLookupOptions,
              isLoadingLookups: loadingLookups, 
              theme: safeTheme,
              primaryKey,
              isProcessing: loading,
              onViewRelatedItems: relatedItemsHandler, // Specific to edit
              relatedItemsButtonText: relatedButtonText, // Specific to edit
              formLayoutConfig, // NUOVO: Passa la configurazione del layout
              isFieldVisible: isFormFieldVisible,
            })
          : ( // Fallback to generic EditRecordForm if no detailViewComponent is passed
            <EditRecordForm
              ref={editFormRef}
              key={selectedRow[primaryKey]}
              id={`edit-form-${selectedRow[primaryKey]}`}
              selectedRow={selectedRow}
              onUpdate={handleUpdateSubmit}
              onDelete={enableDelete ? handleDeleteSubmit : undefined}
              onCancel={handleCancelEdit}
              onMinimize={() => handleMinimizeToggle(selectedRow[primaryKey])}
              columnProps={formColumnProps}
              inputTypes={inputTypes}
              lookupOptions={safeLookupOptions}
              isLoadingLookups={loadingLookups} 
            theme={safeTheme}
            primaryKey={primaryKey}
            isProcessing={loading}
            onViewRelatedItems={relatedItemsHandler} 
            relatedItemsButtonText={relatedButtonText} 
            formLayoutConfig={formLayoutConfig} // AGGIUNGI: Passa la configurazione del layout
            renderCustomFormActions={renderCustomFormActions} // NUOVO: Passa callback per pulsanti custom
            onFieldChange={onFieldChange} // NUOVO: Passa callback per side-effects
            isFieldVisible={isFormFieldVisible}
          />
        )}
        </div>
      )}

      {/* Quick Save Toolbar - Always visible when editing */}
      {enableEdit && selectedRow && (
        <div className="fixed top-20 right-4 z-40 pointer-events-none">
          <div className="pointer-events-auto">
            <MinimizedEditForm
              record={selectedRow}
              theme={safeTheme}
              primaryKey={primaryKey}
              columnProps={formColumnProps}
              onSave={() => {
                // Triggera direttamente il submit del form di modifica corrente

                
                if (editFormRef.current && editFormRef.current.handleSubmit) {

                  editFormRef.current.handleSubmit();
                } else {
                  console.error('[QuickSave] EditFormRef o handleSubmit non disponibili!');
                }
              }}
              onCancel={() => handleCancelEdit()}
              isProcessing={isProcessingUpdate}
              singularRecordName={singularRecordName}
              showOnlyToolbar={true} // NUOVO: Mostra solo la toolbar
            />
          </div>
        </div>
      )}

      {/* --- Remove ViewRowDetailsModal ---
      <ViewRowDetailsModal
          isOpen={isViewModalOpen}
          onClose={handleCloseViewModal}
          row={viewModalData}
          columnProps={columnProps}
          primaryKey={primaryKey}
          theme={safeTheme}
      />
      */}

      {/* --- Render InlineRowDetailView --- */}
      {(() => {

        return isDetailViewOpen && detailViewData && detailViewPosition ? (
          <RowActionPopup
            row={detailViewData} // CORRETTO: era contextMenuRow
            position={detailViewPosition} // CORRETTO: era contextMenuPosition
            onClose={handleCloseDetailView} // CORRETTO: era handleContextMenuClose
            onDuplicate={handleDuplicateRecord}
            onDelete={handleDeleteFromPopup} // CORREZIONE: Era handleDeleteRecord
            theme={safeTheme}
            primaryKey={primaryKey}
            columnProps={columnProps}
            tableName={tableName} // AGGIUNTO: Passa il nome della tabella
          />
        ) : null;
      })()}

    </div>
  );
};

// Wrap with Provider (Updated to pass defaultSortField and defaultSortDirection)
const TableManagerWrapper = (props) => (
  <TableContextProvider
    tableName={props.tableName}
    apiEndpoint={props.apiEndpoint}
    primaryKey={props.primaryKey}
    initialFilters={props.initialFilters}
    autoSearchOnInitialFilters={props.autoSearchOnInitialFilters}
    autoSearchOnLoad={props.autoSearchOnLoad} // ✅ AGGIUNTO: Passa la prop al context
    defaultSortField={props.defaultSortField}
    defaultSortDirection={props.defaultSortDirection}
    pinnedTopFilters={props.pinnedTopFilters}
    // ✅ NUOVO: Passa initialSortBy al context
    initialSortBy={props.initialSortBy}
  >
    <TableManager {...props} />
  </TableContextProvider>
);

export default TableManagerWrapper;

// Helper to normalize lookup options (ensure it's defined or imported)
